import clsx from 'clsx';
import { IconButton } from '@nula/core-ui';
import styles from './message-toolbar.module.css';

export type FeedbackVote = 'up' | 'down';

interface MessageToolbarProps {
  text: string;
  /** Called when the user picks a feedback vote. Parent opens the modal. */
  onVote?: (vote: FeedbackVote) => void;
  className?: string;
}

/**
 * Renders the per-message action toolbar: copy + thumbs up / down.
 *
 * Thumbs no longer toggle local vote state or apply color — clicking
 * either thumb fires `onVote(direction)`, and the parent opens a
 * <FeedbackModal /> to capture the optional comment. The modal is the
 * only user-visible feedback affordance; we don't pre-color the icon
 * because the submission is asynchronous and may fail.
 */
export function MessageToolbar({ text, onVote, className }: MessageToolbarProps) {
  function handleCopy() {
    navigator.clipboard.writeText(text);
  }

  return (
    <div className={clsx(styles.root, className)}>
      <IconButton
        size="sm"
        transparent
        aria-label="Copy"
        onClick={handleCopy}
        className={styles.btn}
      >
        <CopyIcon />
      </IconButton>

      <IconButton
        size="sm"
        transparent
        aria-label="Good response"
        onClick={() => onVote?.('up')}
        className={styles.btn}
      >
        <ThumbUpIcon />
      </IconButton>

      <IconButton
        size="sm"
        transparent
        aria-label="Bad response"
        onClick={() => onVote?.('down')}
        className={styles.btn}
      >
        <ThumbDownIcon />
      </IconButton>
    </div>
  );
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="5" width="9" height="9" rx="1.5" />
      <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" />
    </svg>
  );
}

function ThumbUpIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 7.5 7.5 2a1.5 1.5 0 0 1 1.5 1.5v3h3a1 1 0 0 1 1 1.1l-.8 5A1 1 0 0 1 11.2 13.5H5V7.5Z" />
      <path d="M5 7.5H3a1 1 0 0 0-1 1V13a1 1 0 0 0 1 1h2" />
    </svg>
  );
}

function ThumbDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 8.5 8.5 14a1.5 1.5 0 0 1-1.5-1.5v-3H4a1 1 0 0 1-1-1.1l.8-5A1 1 0 0 1 4.8 2.5H11V8.5Z" />
      <path d="M11 8.5H13a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1h-2" />
    </svg>
  );
}
